import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  Typography,
  Autocomplete,
  DialogTitle, 
  DialogContent, 
  DialogActions,
  AccordionDetails,
  AccordionSummary,
  Accordion,
  Stack,
  Menu,
  MenuItem, 
} from '@mui/material';
import {
  Close as CloseIcon,
  Search as SearchIcon,
  Add as AddIcon,
  SyncAlt as SyncAltIcon,
  Delete as DeleteIcon,
  MoreVert as SettingIcon
} from '@mui/icons-material';
import { getReportById, getReportsList } from '../../config/services/reports';
import { getMeasuresList } from '../../config/services/measures';
import _ from 'lodash';
import toast from 'react-hot-toast';


/*
  DOCUMENTATION:
  hierarchy: MergeReportSetting → MergeReportsContainer → MergeReportDialog → SingleReportOptions → ReportFields
*/


// Join Configuration Modal Component
const JoinConfigModal = ({ open, onClose, onSave, report1, report2, mergeConfig}) => {
  const [mappings, setMappings] = useState([]);
  const [leftSelectedMeasure, setLeftSelectedMeasure] = useState(null);
  const [rightSelectedMeasure, setRightSelectedMeasure] = useState(null);
  const [measuresReport1Dataset, setMeasuresReport1Dataset] = useState([]);
  const [measuresReport2Dataset, setMeasuresReport2Dataset] = useState([]);

  useEffect(() => {
    // Initialize mappings when modal opens
    if (open) {
      setMappings(mergeConfig?.joinConfiguration || []);
    }
  }, [open]);

  const handleAddMapping = () => {
    if (leftSelectedMeasure && rightSelectedMeasure) {
      setMappings([...mappings, { leftField: leftSelectedMeasure, rightField: rightSelectedMeasure }]);
      setLeftSelectedMeasure(null);
      setRightSelectedMeasure(null);
    }
  };

  const handleDeleteMapping = (index) => {
    const updatedMappings = [...mappings];
    updatedMappings.splice(index, 1);
    setMappings(updatedMappings);
  };

  const debounceFetchMeasures = _.debounce((reportType) => {
    fetchMeasures(reportType);
  }, 800);

  const fetchMeasures = async(reportType) => {
    const dataSetId = reportType === 'report1' ? report1?.dataSetId : report2?.dataSetId;
    if (!dataSetId) return;
    
    if (reportType === 'report1') {
      const measures = [...mergeConfig?.selectedFields?.primaryReport];
      setMeasuresReport1Dataset(measures);
    } else {
      const measures = [...mergeConfig?.selectedFields?.secondaryReport];
      setMeasuresReport2Dataset(measures);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Typography variant="h6" fontWeight="bold">Configure Join</Typography>
      </DialogTitle>
      
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Configure how the reports should be joined together.
        </Typography>

        {mappings.map((mapping, index) => (
          <Grid container spacing={2} alignItems="center" key={index} sx={{ mb: 2 }}>
            <Grid item xs={5}>
              <TextField
                fullWidth
                size="small"
                disabled={true}
                value={mapping.leftField.displayName || mapping.leftField.fieldName.split('.')[1]}
                sx={{ bgcolor: 'white', mb: 1, '& .MuiOutlinedInput-root': { borderRadius: '5px' } }}
              />
            </Grid>
            
            <Grid item xs={1} sx={{ textAlign: 'center' }}>
              <SyncAltIcon color="action" />
            </Grid>
            
            <Grid item xs={5}>
              <TextField
                fullWidth
                disabled={true}
                size="small"
                value={mapping.rightField.displayName || mapping.rightField.fieldName.split('.')[1]}
                sx={{ bgcolor: 'white', mb: 1, '& .MuiOutlinedInput-root': { borderRadius: '5px' } }}
              />
            </Grid>
            <Grid item xs={1}>
              <IconButton onClick={() => handleDeleteMapping(index)}>
                <DeleteIcon />
              </IconButton>
            </Grid>
          </Grid>
        ))}

        <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <Grid item xs={5}>
            <Autocomplete
              fullWidth
              size="small"
              freeSolo
              options={measuresReport1Dataset || []}
              value={leftSelectedMeasure}
              getOptionLabel={(option) => option.displayName || option.fieldName.split(".")[1]}
              onChange={(event, newValue) => setLeftSelectedMeasure(newValue)}
              onInputChange={(event, newValue) => debounceFetchMeasures('report1')}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Search"
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" sx={{ color: '#999' }} />
                        </InputAdornment>
                        {params.InputProps.startAdornment}
                      </>
                    ),
                  }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '5px' } }}
                />
              )}
            />
          </Grid>
          
          <Grid item xs={1} sx={{ textAlign: 'center' }}>
            <SyncAltIcon color="action" />
          </Grid>
          
          <Grid item xs={5}>
            <Autocomplete
              fullWidth
              size="small"
              freeSolo
              options={measuresReport2Dataset || []}
              value={rightSelectedMeasure}
              getOptionLabel={(option) => option.displayName || option.fieldName.split('.')[1]}
              onChange={(event, newValue) => setRightSelectedMeasure(newValue)}
              onInputChange={(event, newValue) => debounceFetchMeasures('report2')}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Search"
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" sx={{ color: '#999' }} />
                        </InputAdornment>
                        {params.InputProps.startAdornment}
                      </>
                    ),
                  }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '5px' } }}
                />
              )}
            />
          </Grid>
          <Grid item xs={1}>
            <IconButton onClick={handleAddMapping}>
              <AddIcon />
            </IconButton>
          </Grid>
        </Grid>
      </DialogContent>
      
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} variant="outlined" color="primary">
          Cancel
        </Button>
        <Button 
          onClick={() => onSave(mappings)} 
          color="primary"
          variant="contained"
          sx={{ 
            bgcolor: 'primary.main', 
            '&:hover': { bgcolor: 'primary.dark' } 
          }}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Report Fields Component
const ReportFields = ({ 
  title, 
  fields, 
  onFieldClick, 
  searchValue, 
  onSearchChange,
  placeholder = "Search"
}) => {
  return (
    <Box sx={{ border: '1px solid #E0E0E0', p: 2, bgcolor: '#f9f9f9' }}>
      <Box mb={1}>
        <Typography variant="body2" fontWeight="500">{title}</Typography>
      </Box>
      
      {searchValue !== undefined && (
        <TextField
          fullWidth
          size="small"
          placeholder={placeholder}
          value={searchValue}
          onChange={(e) => onSearchChange(e.target.value)}
          sx={{ mb: 1, '& .MuiOutlinedInput-root': { borderRadius: '5px' } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" sx={{ color: '#999' }} />
              </InputAdornment>
            ),
          }}
        />
      )}
      
      {fields.map((field, index) => (
        <TextField
          key={index}
          fullWidth
          size="small"
          value={field?.name??(field?.displayName || field.fieldName.split('.').pop())}
          onClick={() => onFieldClick(field)}
          InputProps={{
            readOnly: true,
          }}
          sx={{ 
            bgcolor: 'white', 
            mb: 1, 
            '& .MuiOutlinedInput-root': { 
              borderRadius: '5px',
              '&:hover': {
                cursor: 'pointer',
                bgcolor: '#f5f5f5'
              }
            },
            '& .MuiInputBase-input': {
              cursor: 'pointer'
            }
          }}
        />
      ))}
      {fields.length<3 && Array.from({ length: 3-fields.length }).map((_, index) => (
        <TextField
          key={index}
          fullWidth
          size="small"
          value=""
          InputProps={{
            readOnly: true,
          }}
          sx={{ 
            bgcolor: 'white', 
            mb: 1, 
            '& .MuiOutlinedInput-root': { 
              borderRadius: '5px',
            },
            '& .MuiInputBase-input': {
              cursor: 'pointer'
            }
          }}
        />
      ))}
    </Box>
  );
};

// Single Report Options Component
const SingleReportOptions = ({
  report,
  joinKeys,
  selectedFields,
  availableFields,
  availableKeysSearch,
  onAvailableKeysSearchChange,
  onFieldSelect,
  onFieldDeselect,
  reportNum
}) => {
  if (!report) {
    return (
      <Grid container>
        <Grid item xs={12}>
          <Box 
            sx={{ 
              border: '1px solid #E0E0E0', 
              p: 2, 
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '600px',
              bgcolor: '#ffff'
            }}
          >
            <Typography 
              variant="h4" 
              color="text.primary"
              align="center"
              sx={{ fontWeight: 600 }}
            >
              Select Report <br /> to Merge
            </Typography>
          </Box>
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid container>
      <Grid item xs={12} md={6}>
        <Grid container>
          <Grid item xs={12} md={12}>
            <ReportFields 
              title="Join Keys"
              fields={joinKeys}
              onFieldClick={() => {}}
            />
          </Grid>
          <Grid item xs={12} md={12}>
            <ReportFields 
              title="Selected Fields"
              fields={selectedFields}
              onFieldClick={(field) => onFieldDeselect(field, reportNum)}
            />
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs={12} md={6}>
        <ReportFields 
          title="Available Fields"
          fields={availableFields}
          onFieldClick={(field) => onFieldSelect(field, reportNum)}
          searchValue={availableKeysSearch}
          onSearchChange={onAvailableKeysSearchChange}
        />
      </Grid>
    </Grid>
  );
};

// Merge Report Dialog Component
const MergeReportDialog = ({ 
  currentConfig, 
  onSave, 
  index,
  populate = false,
  fetchReportById,
  primaryReportId,
  canDelete = false,
  handleDeleteConfig
}) => {
  const [reportOptions1, setReportOptions1] = useState([]);
  const [reportOptions2, setReportOptions2] = useState([]);
  const [joinModalOpen, setJoinModalOpen] = useState(false);
  const [dataGroupingDropdownOpen, setDataGroupingDropdownOpen] = useState(false);
  const [dataGroupingAnchorEl, setDataGroupingAnchorEl] = useState(null);
  const [selectedFieldForGrouping, setSelectedFieldForGrouping] = useState(null);
  
  const [mergeConfig, setMergeConfig] = useState({
    isEnabled: false,
    secondaryReportId: null,
    primaryReportId: primaryReportId,
    groupConfiguration: {},
    joinConfiguration: [],
    selectedFields: {
      primaryReport: [],
      secondaryReport: []
    },
    availableFields: {
      primaryReport: [],
      secondaryReport: []
    }
  });

  const debounceFetchReportsList = _.debounce((value, setReportOptions) => {
    fetchReportsList({ searchText: value, setReportOptions });
  }, 800);

  const fetchReportsList = async ({searchText, setReportOptions}) => {
    let params = { pageNo: 0, count: 100, search: searchText };
    const response = await getReportsList(params);
    setReportOptions(response.result);
  };

  const handleFieldSelect = (field, reportNum) => {
    if (reportNum === 'report1') {
      setMergeConfig(prev => ({
        ...prev,
        selectedFields: {
          ...prev.selectedFields,
          primaryReport: [
            ...prev.selectedFields.primaryReport,
            {
              fieldId: field.fieldId,
              fieldName: field.fieldName,
              displayName: field.displayName,
              keyType: field.keyType
            }
          ]
        },
        availableFields: {
          ...prev.availableFields,
          primaryReport: prev.availableFields.primaryReport.filter(f => f.fieldId !== field.fieldId)
        }
      }));
    } else if (reportNum === 'report2') {
      setMergeConfig(prev => ({
        ...prev,
        selectedFields: {
          ...prev.selectedFields,
          secondaryReport: [
            ...prev.selectedFields.secondaryReport,
            {
              fieldId: field.fieldId,
              fieldName: field.fieldName,
              displayName: field.displayName,
              keyType: field.keyType 
            }
          ]
        },
        availableFields: {
          ...prev.availableFields,
          secondaryReport: prev.availableFields.secondaryReport.filter(f => f.fieldId !== field.fieldId)
        }
      }));
    }
  };

  const handleFieldDeselect = (field, reportNum) => {
    if (reportNum === 'report1') {
      const searchValue = mergeConfig.report1AvailableKeysSearch || '';
      if((searchValue && field?.fieldName?.toLowerCase()?.includes(searchValue.toLowerCase())) || !searchValue) {
        const array = [...mergeConfig.availableFields.primaryReport, field];
        array.sort((a,b) => a.fieldName.localeCompare(b.fieldName));
        
        setMergeConfig(prev => ({
          ...prev,
          availableFields: {
            ...prev.availableFields,
            primaryReport: array
          },
          selectedFields: {
            ...prev.selectedFields,
            primaryReport: prev.selectedFields.primaryReport.filter(f => f.fieldId !== field.fieldId)
          }
        }));
      }
    } else if (reportNum === 'report2') {
      const searchValue = mergeConfig.report2AvailableKeysSearch || '';
      if((searchValue && field?.fieldName?.toLowerCase()?.includes(searchValue?.toLowerCase())) || !searchValue) {
        const array = [...mergeConfig.availableFields.secondaryReport, field];
        array.sort((a,b) => a.fieldName.localeCompare(b.fieldName));
        
        setMergeConfig(prev => ({
          ...prev,
          availableFields: {
            ...prev.availableFields,
            secondaryReport: array
          },
          selectedFields: {
            ...prev.selectedFields,
            secondaryReport: prev.selectedFields.secondaryReport.filter(f => f.fieldId !== field.fieldId)
          }
        }));
      }
    }
  };

  const handleAvailableFieldsSearch = (searchValue, reportNum) => {
    if (reportNum === 'report1') {
      setMergeConfig(prev => ({
        ...prev,
        report1AvailableKeysSearch: searchValue
      }));
      
      let availableFields = mergeConfig?.availableFields?.primaryReport.map(obj => ({
        ...obj,
        name: obj?.displayName || obj.fieldName.split('.').pop()
      }));

      availableFields = availableFields
        .filter(measure => measure.name.toLowerCase().includes(searchValue.toLowerCase()))
        // .filter(measure => !mergeConfig.selectedFields.primaryReport?.map(field => field._id).includes(measure._id));
      
      availableFields.sort((a,b) => a.name.localeCompare(b.name));
      
      setMergeConfig(prev => ({
        ...prev,
        availableFields: {
          ...prev.availableFields,
          primaryReport: availableFields
        }
      }));
    } else if (reportNum === 'report2') {
      setMergeConfig(prev => ({
        ...prev,
        report2AvailableKeysSearch: searchValue
      }));
      
      let availableFields = mergeConfig?.availableFields?.secondaryReport.map(obj => ({
        ...obj,
        name: obj?.displayName || obj.fieldName.split('.').pop()
      }));

      availableFields = availableFields
        .filter(measure => measure.name.toLowerCase().includes(searchValue.toLowerCase()))
        // .filter(measure => !mergeConfig.selectedFields.secondaryReport?.map(field => field._id).includes(measure._id));
      
      availableFields.sort((a,b) => a.name.localeCompare(b.name));
      
      setMergeConfig(prev => ({
        ...prev,
        availableFields: {
          ...prev.availableFields,
          secondaryReport: availableFields
        }
      }));
    }
  };

  const handleJoinSave = (mappings) => {
    const formattedMappings = mappings.map(mapping => ({
      leftField: {
        fieldId: mapping.leftField.fieldId,
        fieldName: mapping.leftField.fieldName,
        displayName: mapping.leftField.displayName,
        keyType: mapping.leftField.keyType
      },
      rightField: {
        fieldId: mapping.rightField.fieldId,
        fieldName: mapping.rightField.fieldName,
        displayName: mapping.rightField.displayName,
        keyType: mapping.rightField.keyType
      }
    }));
    setJoinModalOpen(false);
    
    setMergeConfig(prev => ({
      ...prev,
      joinConfiguration: formattedMappings
    }));
  };

  const handleSelectReport = (report, reportType) => {
    if (!report) return;
    
    let selectedReport = report;
    selectedReport.measures = selectedReport.measures.map(measure => ({
      ...measure,
      keyType: 'measure'
    }));
    selectedReport.dimensions = selectedReport.dimensions.map(dimension => ({
      ...dimension,
      keyType: 'dimension'
    }));
    
    const availableFields = [...selectedReport.dimensions, ...selectedReport.measures].map(obj => ({
      ...obj,
      name: obj.displayName || obj.fieldName.split('.').pop()
    }));

    let reportObj = {
      dataSetId: selectedReport?.dataSetId?._id,
      reportId: selectedReport?._id,
      reportName: selectedReport?.reportName,
    }
    
    if (reportType === 'report1') {
      setMergeConfig(prev => ({
        ...prev,
        report1: reportObj,
        isEnabled: true,
        primaryReportId: selectedReport._id,
        availableFields: {
          ...prev.availableFields,
          primaryReport: availableFields
        },
        selectedFields: {
          ...prev.selectedFields,
          primaryReport: [],
        },
        joinConfiguration: [],
        groupConfiguration: {}
      }));
    } else if (reportType === 'report2') {
      setMergeConfig(prev => ({
        ...prev,
        report2: reportObj,
        isEnabled: true,  
        secondaryReportId: selectedReport._id,
        availableFields: {
          ...prev.availableFields,
          secondaryReport: availableFields
        },
        selectedFields:{
          ...prev.selectedFields,
          secondaryReport: [],
        },
        joinConfiguration: [],
        groupConfiguration: {}
      }));
    }
  };

  useEffect(() => {
    if (currentConfig) {
      setMergeConfig(currentConfig);
      if (currentConfig.primaryReportId && !currentConfig.report1 ) {
        fetchReportById({reportId:currentConfig.primaryReportId, reportType: 'report1',index});
      }
      if (currentConfig.secondaryReportId && !currentConfig.report2) {
        fetchReportById({reportId:currentConfig.secondaryReportId, reportType: 'report2',index});
      }
    }
  }, [currentConfig]);

  const handleSaveMergeReport = () => {
    const isConfigArrayEmpty = mergeConfig?.joinConfiguration?.length === 0;
    if (isConfigArrayEmpty) {
      return toast.error("Configure join before saving");
    }
    toast.success("Configuration saved successfully.");
    onSave(mergeConfig, index);
  };

  const handleDataGroupingSave = (groupingType) => {
    setMergeConfig((prev) => ({
      ...prev,
      groupConfiguration: {
        ...prev.groupConfiguration,
        [selectedFieldForGrouping?.fieldName]: groupingType,
      },
    }));
    setDataGroupingDropdownOpen(false);
    setDataGroupingAnchorEl(null);
  };

  const DataGroupingDropDown = ({
    open,
    onClose,
    onSave,
    dataGroupingAnchorEl,
  }) => {
    const selectedGroup =
      mergeConfig?.groupConfiguration?.[selectedFieldForGrouping?.fieldName] ||
      "";
    const groupingOptions = [
      "CSV",
      "SUM",
      "MIN",
      "MAX",
      "AVG",
      "COUNT",
      "COUNT_DISTINCT",
    ];
    return (
      <Box>
        <Menu
          open={open}
          onClose={onClose}
          anchorEl={dataGroupingAnchorEl}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
        >
          <MenuItem
            disabled
            style={{
              minHeight: "60px",
              color: "#212b36",
              fontWeight: 600,
              fontSize: "16px",
            }}
          >
            All matches in Single row
          </MenuItem>
          {groupingOptions.map((option) => (
            <MenuItem
              key={option}
              selected={selectedGroup === option}
              onClick={() => handleDataGroupingSave(option)}
            >
              {option}
            </MenuItem>
          ))}
          <MenuItem selected={selectedGroup === ""} onClick={() => onSave("")}>
            ------------- None --------------
          </MenuItem>
        </Menu>
      </Box>
    );
  };

  

  return (
    <>
      <Paper sx={{ borderRadius: 1, overflow: 'hidden' }}>
        <Box p={2}>
          {/* Report Selection Row */}
          <Grid container mb={2}>
            <Grid item xs={12} md={4}>
              <Autocomplete
                fullWidth
                size="small"
                options={reportOptions1}
                disabled={true}
                getOptionLabel={(option) => option.reportName || ''}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Search Report 1"
                    sx={{ bgcolor: '#f9f9f9' }}
                  />
                )}
                value={mergeConfig.report1 || null}
                onChange={(event, newValue) => handleSelectReport(newValue, 'report1')}
                onInputChange={(event, newInputValue) => debounceFetchReportsList(newInputValue, setReportOptions1)}
              />
            </Grid>
            <Grid item xs={12} md={1}></Grid>
            <Grid item xs={12} md={4}>
              <Autocomplete
                fullWidth
                size="small"
                options={reportOptions2}
                getOptionLabel={(option) => option.reportName || ''}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Search Report 2"
                    sx={{ bgcolor: '#f9f9f9' }}
                  />
                )}
                value={mergeConfig.report2 || null}
                onChange={(event, newValue) => handleSelectReport(newValue, 'report2')}
                onInputChange={(event, newInputValue) => debounceFetchReportsList(newInputValue, setReportOptions2)}
              />
            </Grid>
            <Grid item xs={12} md={1}></Grid>
          </Grid>

          {/* Main Content Columns */}
          <Grid container>
            {/* Left Column */}
            <Grid item xs={12} md={4}>
              <SingleReportOptions
                report={mergeConfig.report1}
                joinKeys={mergeConfig.joinConfiguration?.map(mapping => mapping.leftField) || []}
                selectedFields={mergeConfig.selectedFields.primaryReport || []}
                availableFields={mergeConfig.availableFields.primaryReport || []}
                availableKeysSearch={mergeConfig.report1AvailableKeysSearch || ''}
                onAvailableKeysSearchChange={(value) => handleAvailableFieldsSearch(value, 'report1')}
                onFieldSelect={(field) => handleFieldSelect(field, 'report1')}
                onFieldDeselect={(field) => handleFieldDeselect(field, 'report1')}
                reportNum="report1"
              />
            </Grid>

            {/* Configure Join Button */}
            <Grid item xs={12} md={1} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start', mt: 3 }}>
              <Button
                variant="outlined"
                onClick={() => setJoinModalOpen(true)}
                disabled={!(mergeConfig.report1?.dataSetId && mergeConfig.report2?.dataSetId)}
                sx={{
                  height: 'fit-content',
                  p: 1,
                  minWidth: 'unset',
                  borderRadius: 1,
                  textTransform: 'none',
                  color: '#FF5722',
                  borderColor: '#FF5722',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  '&:hover': { borderColor: '#FF5722', bgcolor: 'rgba(255, 87, 34, 0.04)' }
                }}
              >
                <AddIcon sx={{ fontSize: 16 }} />
                <Typography variant="caption" sx={{ textAlign: 'center', lineHeight: 1.2 }}>
                  Configure Join
                </Typography>
              </Button>
            </Grid>

            {/* Right Column */}
            <Grid item xs={12} md={4}>
              <SingleReportOptions
                report={mergeConfig.report2}
                joinKeys={mergeConfig.joinConfiguration?.map(mapping => mapping.rightField) || []}
                selectedFields={mergeConfig.selectedFields.secondaryReport || []}
                availableFields={mergeConfig.availableFields.secondaryReport || []}
                availableKeysSearch={mergeConfig.report2AvailableKeysSearch || ''}
                onAvailableKeysSearchChange={(value) => handleAvailableFieldsSearch(value, 'report2')}
                onFieldSelect={(field) => handleFieldSelect(field, 'report2')}
                onFieldDeselect={(field) => handleFieldDeselect(field, 'report2')}
                reportNum="report2"
              />
            </Grid>
            
            <Grid item xs={12} md={1}></Grid>
            
            {/* Selected Fields Column */}
            <Grid item xs={12} md={2}>
              <Box sx={{ border: '1px solid #E0E0E0', bgcolor: '#FFF5F0', p: 2, borderRadius: 1 }}>
                <Box mb={1}>
                  <Typography variant="body2" fontWeight="500">Fields</Typography>
                </Box>
                <Box>
                  {[...(mergeConfig.selectedFields.primaryReport || [])]?.map((field, index) => (
                    <TextField
                      key={index}
                      fullWidth
                      size="small"
                      value={field?.displayName || field.fieldName.split('.').pop()}
                      InputProps={{
                        readOnly: true,
                      }}
                      sx={{ bgcolor: 'white', mb: 1, '& .MuiOutlinedInput-root': { borderRadius: '5px' } }}
                    />
                  ))}
                  {[...(mergeConfig.selectedFields.secondaryReport || [])]?.map((field, index) => (
                    <TextField
                      key={index}
                      fullWidth
                      size="small"
                      value={field?.displayName || field.fieldName.split('.').pop()}
                      InputProps={{
                        readOnly: true,
                        endAdornment: (
                           <IconButton onClick={(event)=>{
                            setDataGroupingAnchorEl(event.currentTarget);
                            setDataGroupingDropdownOpen(true);
                            setSelectedFieldForGrouping(field);
                          }} edge="end">
                            <SettingIcon />
                          </IconButton>
                        ),
                      }}
                      sx={{ bgcolor: 'white', mb: 1, '& .MuiOutlinedInput-root': { borderRadius: '5px', paddingRight: 0.5 } }}
                    />
                  ))}
                  <DataGroupingDropDown open={dataGroupingDropdownOpen} onClose={()=>setDataGroupingDropdownOpen(false)} onSave={handleDataGroupingSave} dataGroupingAnchorEl={dataGroupingAnchorEl}/>
                </Box>
                <Box mt={2}>
                  <Stack spacing={1}>
                    <Button
                      onClick={handleSaveMergeReport}
                      fullWidth
                      variant="contained"
                      sx={{
                        bgcolor: '#DDDDDD',
                        color: '#666666',
                        '&:hover': { bgcolor: '#CCCCCC' },
                        textTransform: 'none',
                        boxShadow: 'none'
                      }}
                    >
                      Save
                    </Button>
                    {canDelete && <Button
                      onClick={() => handleDeleteConfig(index)}
                      fullWidth
                      variant="contained"
                      sx={{
                        bgcolor: '#DDDDDD',
                        color: '#666666',
                        '&:hover': { bgcolor: '#CCCCCC' },
                        textTransform: 'none',
                        boxShadow: 'none'
                      }}
                    >
                      Delete
                    </Button>}
                  </Stack>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Paper>
      
      <JoinConfigModal 
        open={joinModalOpen} 
        onClose={() => setJoinModalOpen(false)} 
        onSave={handleJoinSave} 
        report1={mergeConfig.report1} 
        report2={mergeConfig.report2} 
        mergeConfig={mergeConfig}
      />
    </>
  );
};

// Merge Reports Container Component
const MergeReportsContainer = ({ open, onClose, reportDetails, updateReportDetails }) => {
  console.log(reportDetails,'------reportDetails')
  const defaultConfig = {
    isEnabled: false,
    primaryReportId: reportDetails?.reportDetails?._id,
    secondaryReportId: null,
    joinConfiguration: [],
    groupConfiguration: {},
    selectedFields: {
      primaryReport: [],
      secondaryReport: []
    },
    availableFields: {
      primaryReport: [],
      secondaryReport: []
    }
  };
  
  const [mergedReportsArray, setMergedReportsArray] = useState([defaultConfig]);

  useEffect(()=>{
    if(reportDetails?.reportDetails?.mergeReport?.length > 0) {
      console.log('----1')
      initializeMergeReportsArray(reportDetails?.reportDetails?.mergeReport);
    }else{
      setMergedReportsArray([defaultConfig]);
    }
  },[reportDetails?.reportDetails?.mergeReport])

  const initializeMergeReportsArray = async(mergeReportArray) => {
    let newMergeReportArray = [...mergeReportArray];

    let index = newMergeReportArray.length-1;
    let primaryAvailableFields = [...newMergeReportArray[index].availableFields.primaryReport, ...newMergeReportArray[index].availableFields.secondaryReport];
    let primarySelectedFields = [...newMergeReportArray[index].selectedFields.primaryReport, ...newMergeReportArray[index].selectedFields.secondaryReport];
    const updatedDefaultConfig = {
      ...defaultConfig,
      availableFields:{
        ...defaultConfig.availableFields,
        primaryReport: [...primaryAvailableFields],
      },
      selectedFields:{
        ...defaultConfig.selectedFields,
        primaryReport: [...primarySelectedFields],
      },
      report1: newMergeReportArray[index].report1,
    }
    newMergeReportArray.push(updatedDefaultConfig);
    setMergedReportsArray(newMergeReportArray);
  }

  const fetchReportById = async ({reportId, reportType,index, stateUpdate = true, localMergeReportArray = mergedReportsArray}) => {
    if(!reportId) return;
    const response = await getReportById({reportId});
    let selectedReport = response.result;
    selectedReport.measures = selectedReport.measures.map(measure => ({
      ...measure,
      keyType: 'measure'
    }));
    selectedReport.dimensions = selectedReport.dimensions.map(dimension => ({
      ...dimension,
      keyType: 'dimension'
    }));

    let availableFields = [...selectedReport.dimensions, ...selectedReport.measures].map(obj => ({
      ...obj,
      name: obj.displayName || obj.fieldName.split('.').pop()
    }));

    let reportObj = {
      dataSetId: selectedReport?.dataSetId?._id,
      reportId: selectedReport?._id,
      reportName: selectedReport?.reportName,
    }
    
    const updatedReports = [...localMergeReportArray];
    
    if (reportType === 'report1') {
      updatedReports[index] = {
        ...updatedReports[index],
        report1: reportObj,
        availableFields: {
          ...updatedReports[index].availableFields,
          primaryReport: availableFields
        }
      };
    } else if (reportType === 'report2') {
      updatedReports[index] = {
        ...updatedReports[index],
        report2: reportObj,
        availableFields: {
          ...updatedReports[index].availableFields,
          secondaryReport: availableFields
        }
      };
    }
    
    if(stateUpdate) {
      setMergedReportsArray(updatedReports);
    }else{
      return updatedReports[index];
    }
  };

  const handleSaveMergeReport = (mergeConfig, index) => {
    const mergeConfigObject = {
      ...mergeConfig,
      id: index,
      isOpen: false,
    };
    
    const updatedReports = [...mergedReportsArray];
    updatedReports[index] = mergeConfigObject;

    if ((updatedReports.length-1) === index) {
      let primaryAvailableFields = [...updatedReports[index].availableFields.primaryReport, ...updatedReports[index].availableFields.secondaryReport];
      let primarySelectedFields = [...updatedReports[index].selectedFields.primaryReport, ...updatedReports[index].selectedFields.secondaryReport];
      const updatedDefaultConfig = {
        ...defaultConfig,
        availableFields:{
          ...defaultConfig.availableFields,
          primaryReport: [...primaryAvailableFields],
        },
        selectedFields:{
          ...defaultConfig.selectedFields,
          primaryReport: [...primarySelectedFields],
        },
        report1: updatedReports[index].report1,
      }
      updatedReports.push(updatedDefaultConfig);
    }

    setMergedReportsArray(updatedReports);
  };

  const handleAccordionClose = (index) => {
    const updatedReports = [...mergedReportsArray];
    updatedReports[index].isOpen = !updatedReports[index].isOpen;
    setMergedReportsArray(updatedReports);
  };

  const handleDeleteConfig = async(index) => {
    if(index === 0) {
      setMergedReportsArray([defaultConfig]);
    }
    else{
      let updatedReports = [...mergedReportsArray];
      updatedReports.splice(index, 1);
      setMergedReportsArray(updatedReports);
    }
  };

  useEffect(() => {
    if(!open){
      const newArray = JSON.parse(JSON.stringify(mergedReportsArray))
      newArray.splice(-1,1)
      const updatedReportDetails = {
        ...reportDetails,
        reportDetails: {
          ...reportDetails.reportDetails,
          mergeReport: newArray
        }
      };
      console.log(updatedReportDetails.mergeReport,'updatedReportDetails')
      updateReportDetails(updatedReportDetails);
    }
  }, [open])

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: 1,
          maxHeight: '90vh',
          m: 0,
          minWidth: '80vw',
          p: 0,
          bgcolor: '#f5f5f5'
        }
      }}
    >
      <Box p={3}>
        {/* Header */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Typography variant="h6" fontWeight="500">
            Merge Reports
          </Typography>
          <IconButton onClick={onClose} size="large" sx={{ p: 0 }}>
            <CloseIcon />
          </IconButton>
        </Box>
        
        {/* Existing Merge Reports */}
        {mergedReportsArray?.length > 1 &&
          mergedReportsArray.map((mergeReport, index) => {
            if (index !== mergedReportsArray.length - 1) {
              return (
                <div style={{ position: "relative" }}>
                  <div
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: "rgba(255, 255, 255, 0.7)",
                      zIndex: 10,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      pointerEvents: "auto",
                    }}
                  >
                  </div>
                  <Accordion
                    key={index}
                    onChange={() => handleAccordionClose(index)}
                  >
                    <AccordionSummary>
                      <Typography variant="h6" fontWeight="500">
                        Merge Report {index + 1}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <MergeReportDialog
                        currentConfig={mergeReport}
                        onSave={handleSaveMergeReport}
                        index={index}
                        populate={true}
                        fetchReportById={fetchReportById}
                        primaryReportId={reportDetails?.reportDetails?._id}
                        canDelete={false}
                        handleDeleteConfig={handleDeleteConfig}
                      />
                    </AccordionDetails>
                  </Accordion>
                </div>
              );
            }
            return null;
          })}

        <Accordion>
          <AccordionSummary>
            <Typography variant="h6" fontWeight="500">
              Main Report
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <MergeReportDialog
              currentConfig={mergedReportsArray[mergedReportsArray.length - 1]}
              onSave={handleSaveMergeReport}
              index={mergedReportsArray.length - 1}
              populate={false}
              fetchReportById={fetchReportById}
              primaryReportId={reportDetails?.reportDetails?._id}
              canDelete={true}
              handleDeleteConfig={handleDeleteConfig}
            />
          </AccordionDetails>
        </Accordion>
        {/* New Merge Report */}
      </Box>
    </Dialog>
  );
};

// Main Component
const MergeReportSetting = ({ reportDetails, updateReportDetails }) => {
  const [open, setOpen] = useState(false);
  
  return (
    <>
      {reportDetails?.reportDetails?._id && reportDetails?.reportDetails?.chartType==='table' && <>
        <Box className="report-new-charts">
          <Box className="filter_accordian">
            <Box className="filter_box_summary" onClick={() => setOpen(true)}>
              <Typography variant='h6' className="font-weight-500 form_input_label">Merge Reports</Typography>
            </Box>
          </Box>
        </Box>
        <MergeReportsContainer 
          open={open} 
          onClose={() => setOpen(false)} 
          reportDetails={reportDetails}
          updateReportDetails={updateReportDetails}
        />
      </>}
    </>
  );
};

export default MergeReportSetting;